import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { Layout } from '@/components/Layout'
import { Dashboard } from '@/pages/Dashboard'
import { Logs } from '@/pages/Logs'
import { ECS } from '@/pages/ECS'
import { CICD } from '@/pages/CICD'
import { Agent } from '@/pages/Agent'
import { Login } from '@/pages/Login'
import { Users } from '@/pages/Users'
import { ApiKeys } from '@/pages/ApiKeys'
import { OpenSearch } from '@/pages/OpenSearch'
import { Vercel } from '@/pages/Vercel'
import { Sentry as SentryPage } from '@/pages/Sentry'
import { Release } from '@/pages/Release'
import { Review } from '@/pages/Review'
import { BugFix } from '@/pages/BugFix'
import { Tasks } from '@/pages/Tasks'
import { Runners } from '@/pages/Runners'
import type { ReactNode } from 'react'

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-[var(--color-text-muted)]">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-[var(--color-text-muted)]">Loading...</div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function FallbackComponent() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
      <div className="text-center">
        <h1 className="text-xl font-bold text-red-500 mb-2">An error occurred</h1>
        <p className="text-[var(--color-text-muted)]">The application encountered an unexpected error.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Reload
        </button>
      </div>
    </div>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/ecs" element={<ECS />} />
              <Route path="/cicd" element={<CICD />} />
              <Route path="/vercel" element={<Vercel />} />
              <Route path="/sentry" element={<SentryPage />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/agent" element={<Agent />} />
              <Route path="/users" element={<Users />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/opensearch" element={<OpenSearch />} />
              <Route path="/release" element={<Release />} />
              <Route path="/review" element={<Review />} />
              <Route path="/bugfix" element={<BugFix />} />
              <Route path="/runners" element={<Runners />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}

export default App
